import { css } from "@emotion/react";
import styled from "@emotion/styled";

import { BaseLink } from "~/components/common/base-link";
import type { LogoVariant } from "~/components/header/types";
import IconSearch from "~/components/home/header/header-icon/search.svg";
import { MiniHeader } from "~/components/home/mini-header";
import { setFullViewportWidthStyles } from "~/lib/styles";
import type { ResponsiveVariants } from "~/lib/styles/responsive";
import { setResponsiveVariantStyles } from "~/lib/styles/responsive";

import LogoCelebrating from "./icon-logo-celebrating.svg";
import LogoFlag from "./icon-logo-flag.svg";
import LogoText from "./icon-logo-text.svg";

export type ActionButtonProps = {
  $responsiveVariants: ResponsiveVariants<"show" | "hidden">;
};

export const Container = styled.div`
  ${setFullViewportWidthStyles()}
`;

export const HomeHeaderContainer = styled.section``;

export const ActionButton = styled.div<ActionButtonProps>`
  line-height: 0;

  cursor: pointer;

  ${props =>
    setResponsiveVariantStyles(props.$responsiveVariants, variant => {
      switch (variant) {
        case "show":
          return css`
            display: block;
          `;
        case "hidden":
          return css`
            display: none;
          `;
      }
    })}
`;

export const AvatarContainer = styled.div``;

export const StyledLogoText = styled(LogoText)`
  display: inline-block;

  inline-size: 173.18px;
  block-size: 16.37px;

  ${props => props.theme.breakpoints.up("tablet")} {
    inline-size: 268.77px;
    block-size: 23.93px;
  }

  ${props => props.theme.breakpoints.up("homeDesktop")} {
    inline-size: 392px;
    block-size: 35px;
  }
`;

export const StyledLogoFlag = styled(LogoFlag)`
  display: inline-block;

  inline-size: 28.43px;
  block-size: 21.54px;

  ${props => props.theme.breakpoints.up("tablet")} {
    inline-size: 49.09px;
    block-size: 37.86px;
  }

  ${props => props.theme.breakpoints.up("homeDesktop")} {
    inline-size: 72px;
    block-size: 56px;
  }
`;

export const LogoTextContainer = styled.div`
  margin-block-start: 5px;
  margin-inline-start: 6.05px;

  ${props => props.theme.breakpoints.up("tablet")} {
    margin-block-start: 5px;
    margin-inline-start: 12.14px;
  }

  ${props => props.theme.breakpoints.up("homeDesktop")} {
    margin-block-start: 5px;
    margin-inline-start: 14px;
  }
`;

export const StyledLogoCelebrating = styled(LogoCelebrating)`
  display: none;

  ${props => props.theme.breakpoints.up("tablet")} {
    display: block;

    inline-size: 117px;
    block-size: 15px;
    margin-block-start: -1px;
  }

  ${props => props.theme.breakpoints.up("homeDesktop")} {
    inline-size: 171px;
    block-size: 21px;
    margin-block-start: -2px;
  }
`;

export type LogoProps = {
  $responsiveVariants: ResponsiveVariants<LogoVariant>;
};
export const StyledLogoLink = styled(BaseLink)`
  position: absolute;
  inset-inline-start: 50%;

  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;

  transform: translateX(-50%);

  ${props => props.theme.breakpoints.up("tablet")} {
    align-items: flex-start;
  }
`;

export const StyleSearchLink = styled(BaseLink)`
  line-height: 0;
`;

export const StyledIconSearch = styled(IconSearch)`
  inline-size: 24px;
  block-size: 24px;
`;

export const HeaderContainer = styled.div`
  position: relative;

  background-color: transparent;

  ${props => props.theme.breakpoints.up("homeDesktop")} {
    border-block-end: 1px solid #000000;
  }
`;

export const Wrapper = styled.div`
  position: relative;

  max-inline-size: 1344px;
  margin-inline: auto;

  ${props => props.theme.breakpoints.up("tablet")} {
    padding-block: 16px 0;
    padding-inline: 0;
  }

  ${props => props.theme.breakpoints.up("homeDesktop")} {
    padding-block: 24px 0;
  }
`;

export const Content = styled.div`
  display: flex;
  flex-flow: row;
  justify-content: space-between;
  align-items: center;

  inline-size: 100%;
  block-size: 45px;
  padding-inline: 20px;
  box-sizing: border-box;

  ${props => props.theme.breakpoints.up("tablet")} {
    block-size: 60px;
    padding-inline: 32px;
  }

  ${props => props.theme.breakpoints.up("homeDesktop")} {
    block-size: 64px;
    margin-block-end: 24px;
  }
`;

export const LeftFeatureContainer = styled.div`
  display: flex;
  flex-flow: row;
  align-items: center;

  & > *:not(:last-child) {
    margin-inline-end: 20px;
  }
`;

export const RightFeatureContainer = styled.div`
  display: flex;
  flex-flow: row;
  align-items: center;

  ${props => props.theme.breakpoints.up("tablet")} {
    ${ActionButton}:not(:first-child) {
      margin-inline-start: 20px;
    }
  }
`;

export const OnelineMenuContainer = styled.div`
  display: flex;
  justify-content: center;

  block-size: 34px;
  margin-inline: 20px;
  padding-block: 10px;

  border-block-end: 1px solid #000000;

  ${props => props.theme.breakpoints.between("tablet", "homeDesktop")} {
    margin-inline: 32px;
  }

  ${props => props.theme.breakpoints.up("homeDesktop")} {
    border-block-end: none;

    padding-block: 8px 10px;
    padding-inline: 0;
  }
`;

type StyledMiniHeaderProps = {
  $shouldSwap: boolean;
};

export const StyledMiniHeader = styled(MiniHeader)<StyledMiniHeaderProps>`
  position: ${props => (props.$shouldSwap ? "fixed" : "relative")};

  display: ${props => (props.$shouldSwap ? "block" : "none")};
`;
